/**
 * Shared resolution of employment type labels (name vs master-config id) for
 * separation tables (Exit approvals, Termination list, etc.).
 * Mirrors how employee profile and GET pending-approvals typically expose data.
 */

/** Resolve display text from string, number, or populated master-config object. */
export function coerceEmploymentTypeText(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "string") {
    const t = v.trim();
    return t || undefined;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v !== "object") return undefined;
  const o = v as Record<string, unknown>;
  for (const k of [
    "employmentTypeName",
    "name",
    "displayName",
    "label",
    "title",
  ]) {
    const inner = o[k];
    if (typeof inner === "string" && inner.trim()) return inner.trim();
  }
  return undefined;
}

/** Explicit display strings (profile: `employmentTypeName`). */
export function pickEmploymentTypeNamesOnly(
  rec: Record<string, unknown>,
): string | undefined {
  for (const k of [
    "employmentTypeName",
    "employment_type_name",
    "employmentTypeLabel",
    "employmentTypeDisplayName",
  ]) {
    const s = coerceEmploymentTypeText(rec[k]);
    if (s) return s;
  }
  const raw = rec.employmentType ?? rec.employment_type;
  if (raw != null && typeof raw === "object") {
    return coerceEmploymentTypeText(raw);
  }
  return undefined;
}

/** Master-config id (profile often falls back to raw `employmentType` string). */
export function pickEmploymentTypeIdOnly(
  rec: Record<string, unknown>,
): string | undefined {
  const raw = rec.employmentType ?? rec.employment_type;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const k of ["_id", "id"]) {
      const v = o[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return undefined;
}

export function collectEmploymentTypeSources(
  row: unknown,
): Record<string, unknown>[] {
  const r = row as Record<string, unknown>;
  const sources: Record<string, unknown>[] = [];
  const emp = r.employee;
  if (emp && typeof emp === "object") {
    const e = emp as Record<string, unknown>;
    sources.push(e);
    const nestedUser = e.user;
    if (nestedUser && typeof nestedUser === "object") {
      sources.push(nestedUser as Record<string, unknown>);
    }
  }
  const rootUser = r.user;
  if (rootUser && typeof rootUser === "object") {
    sources.push(rootUser as Record<string, unknown>);
  }
  sources.push(r);
  return sources;
}

/**
 * Same semantics as profile: prefer `employmentTypeName`, else resolve
 * `employmentType` id via master config (else raw id).
 */
export function employmentTypeDisplayForRow(
  row: unknown,
  employmentTypeIdToLabel: Map<string, string>,
): string {
  const sources = collectEmploymentTypeSources(row);
  for (const src of sources) {
    const n = pickEmploymentTypeNamesOnly(src);
    if (n) return n;
  }
  for (const src of sources) {
    const id = pickEmploymentTypeIdOnly(src);
    if (id && employmentTypeIdToLabel.has(id)) {
      return employmentTypeIdToLabel.get(id)!;
    }
  }
  for (const src of sources) {
    const id = pickEmploymentTypeIdOnly(src);
    if (id) return id;
  }
  return "—";
}

export function buildEmploymentTypeIdToLabelMap(
  employmentTypeMasterList: {
    id?: string;
    displayName?: string;
    _id?: string;
  }[],
): Map<string, string> {
  const m = new Map<string, string>();
  for (const opt of employmentTypeMasterList) {
    const label = String(opt.displayName ?? "").trim();
    if (!label) continue;
    const id = String(opt.id ?? "").trim();
    const oid = String((opt as { _id?: string })._id ?? "").trim();
    if (id) m.set(id, label);
    if (oid && oid !== id) m.set(oid, label);
  }
  return m;
}
